// Pure territory-derivation for the board overlay.
//
// The sim snapshot gives every tile `controlledBy`: a player id, or `null` for
// unclaimed land (0 is player 0 — the human — so it is a real owner, not
// "nobody"). The overlay draws, per owned tile: a soft fill tint, plus
// border segments only on the edges where the controller differs (unowned
// tile, another player, or unexplored space). Pure here so it's testable;
// TerritoryOverlay.tsx does the rendering.

import { playerColor } from '@aod/ui';

/** Sim AXIAL_DIRS order (E, NE, NW, W, SW, SE) — matches the river/bridge
 * edge masks (see BridgeTargetRings). */
export const HEX_DIRS: readonly (readonly [number, number])[] = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

/** Owner id, or the richer controller record the overlay builds. */
type OwnerValue = number | { ownerId: number };

/**
 * The owner id of a map entry, or `undefined` when the tile is not owned.
 *
 * `undefined` — not `0` — because 0 is player 0 (the human): a missing entry
 * means "nobody owns this tile" (unclaimed, fog or off-map), which is
 * different from a tile the human owns.
 */
function ownerIdOf(v: OwnerValue | undefined): number | undefined {
  if (v === undefined) return undefined;
  return typeof v === 'number' ? v : v.ownerId;
}

/** Which edges of an owned tile face a different controller (or nothing
 * known). `owners` maps "q,r" → controlling player; tiles missing from the
 * map (unclaimed / fog / off-map) count as different. */
export function borderDirsOf(
  owners: ReadonlyMap<string, OwnerValue>,
  q: number,
  r: number,
): number[] {
  const own = ownerIdOf(owners.get(`${q},${r}`));
  if (own === undefined) return [];
  const dirs: number[] = [];
  for (let dir = 0; dir < 6; dir++) {
    const [dq, dr] = HEX_DIRS[dir]!;
    const other = ownerIdOf(owners.get(`${q + dq},${r + dr}`));
    if (other !== own) dirs.push(dir);
  }
  return dirs;
}

/** All border segments for a visibility-filtered owner map. */
export function territoryBorders(owners: ReadonlyMap<string, OwnerValue>): TerritorySegment[] {
  const segs: TerritorySegment[] = [];
  for (const [key, value] of owners) {
    const ownerId = ownerIdOf(value);
    if (ownerId === undefined) continue;
    const [q, r] = key.split(',').map(Number) as [number, number];
    for (const dir of borderDirsOf(owners, q, r)) {
      segs.push({ q, r, dir, ownerId });
    }
  }
  return segs;
}

// Dominion-path accents (docs/02-systems/dominion-path-visuals.md §2.1).
// Borders mix the owner's player colour toward the path accent so the
// frontier reads "whose + which path" while ownership stays dominant.
export const PATH_ACCENTS: Record<string, readonly [number, number, number]> = {
  industry: [0.79, 0.54, 0.23],
  science: [0.25, 0.72, 0.79],
  faith: [0.9, 0.76, 0.32],
  arcane: [0.63, 0.42, 0.84],
  nature: [0.35, 0.63, 0.29],
  chaos: [0.79, 0.31, 0.61],
};

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function mixRgb(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/**
 * Border colour for a tile: the owner's player colour mixed ~35% toward the
 * controller's path accent; Dark alignment additionally pulls the mix toward
 * cold near-black (the "evil" read) — ownership hue still leads.
 */
export function borderColorFor(
  ownerId: number,
  archetype: string | undefined,
  alignment: string | undefined,
): string {
  let [r, g, b] = hexToRgb(playerColor(ownerId));
  if (archetype) {
    const accent = PATH_ACCENTS[archetype];
    if (accent) [r, g, b] = mixRgb([r, g, b], accent, 0.35);
  }
  if (alignment === 'dark') {
    [r, g, b] = mixRgb([r, g, b], [0.16, 0.16, 0.2], 0.3);
  }
  const to255 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);
  return `#${((1 << 24) | (to255(r) << 16) | (to255(g) << 8) | to255(b)).toString(16).slice(1)}`;
}

/** World-space offset from a tile's centre to the midpoint of the edge
 * toward neighbour offset (dq, dr) — half the centre-to-centre vector
 * (mirrors axialToWorld: x = √3·(q + r/2), z = 1.5·r). */
export function edgeMidpointOffset(dq: number, dr: number): readonly [number, number] {
  const dx = Math.sqrt(3) * (dq + dr / 2);
  const dz = 1.5 * dr;
  return [dx / 2, dz / 2];
}

/** Yaw (rotation.y) that lays a box's local +X along the edge toward
 * (dq, dr) — i.e. perpendicular to the centre-to-centre direction. */
export function edgeYaw(dq: number, dr: number): number {
  const [ox, oz] = edgeMidpointOffset(dq, dr);
  return Math.atan2(ox, oz);
}

export interface TerritorySegment {
  readonly q: number;
  readonly r: number;
  readonly dir: number;
  readonly ownerId: number;
}
